/**
 * React binding of the URL state codec. Every setter writes the URL
 * (replace, not push, unless `push` is passed) so browser history stays sane
 * while dragging a chart; selection changes push so Back undoes them.
 */
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import type { Currency } from "@/api/types";
import { type Preset } from "@/lib/time";
import {
  EMPTY_FILTERS,
  liveTick,
  nudgeRange,
  parseUrlState,
  resolveRange,
  writeUrlState,
  zoomRange,
  type Filters,
  type ListFilterKey,
  type ResolvedRange,
  type TimeRange,
  type UrlState,
} from "./url";

export interface UrlStateApi {
  state: UrlState;
  /**
   * Concrete [from, to) at call time. A pinned range moves with the clock,
   * but only on the `LIVE_TICK_SECS` grid, so the object identity — and every
   * query key built from it — is stable between ticks.
   */
  range: ResolvedRange;
  setPreset: (p: Preset) => void;
  setRange: (from: number, to: number, opts?: { pinned?: boolean; push?: boolean }) => void;
  setPinned: (pinned: boolean) => void;
  nudge: (fraction: number) => void;
  zoom: (factor: number) => void;
  setBucket: (b: number | undefined) => void;
  setFilters: (patch: Partial<Filters>) => void;
  toggleFilterValue: (key: ListFilterKey, value: string) => void;
  clearFilters: () => void;
  setSelection: (families: string[]) => void;
  toggleSelected: (family: string, exclusive?: boolean) => void;
  clearSelection: () => void;
  setLive: (live: boolean) => void;
  setCurrency: (c: Currency) => void;
  /** Current search string ("?…") — for saved views. */
  search: string;
}

export function useUrlState(): UrlStateApi {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => parseUrlState(params), [params]);
  const tick = liveTick();
  const range = useMemo(() => resolveRange(state.range, tick), [state.range, tick]);

  const update = useCallback(
    (fn: (s: UrlState) => UrlState, push = false) => {
      setParams(
        (prev) => {
          const next = fn(parseUrlState(prev));
          return writeUrlState(next, new URLSearchParams(prev));
        },
        { replace: !push },
      );
    },
    [setParams],
  );

  const setRangeObj = useCallback(
    (r: TimeRange, push = false) => update((s) => ({ ...s, range: r }), push),
    [update],
  );

  return {
    state,
    range,
    search: params.toString() ? `?${params.toString()}` : "",
    setPreset: (p) => setRangeObj({ preset: p, pinned: true }),
    setRange: (from, to, opts) =>
      setRangeObj(opts?.pinned ? { from, pinned: true } : { from, to, pinned: false }, opts?.push ?? false),
    setPinned: (pinned) =>
      update((s) => {
        // every clock read goes through the same grid as `range` above, so
        // the range a nudge / zoom / unpin writes is one the slices may
        // already have prefetched
        const r = resolveRange(s.range, liveTick());
        return {
          ...s,
          range: pinned ? { from: r.from, pinned: true } : { from: r.from, to: r.to, pinned: false },
        };
      }),
    nudge: (fraction) => update((s) => ({ ...s, range: nudgeRange(s.range, fraction, liveTick()) })),
    zoom: (factor) => update((s) => ({ ...s, range: zoomRange(s.range, factor, liveTick()) })),
    setBucket: (b) => update((s) => ({ ...s, bucket: b })),
    setFilters: (patch) => update((s) => ({ ...s, filters: { ...s.filters, ...patch } })),
    toggleFilterValue: (key, value) =>
      update((s) => {
        const cur = s.filters[key] as string[];
        const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
        return { ...s, filters: { ...s.filters, [key]: next } };
      }),
    clearFilters: () => update((s) => ({ ...s, filters: EMPTY_FILTERS })),
    setSelection: (families) =>
      update((s) => ({ ...s, selection: families.map((f) => f.toLowerCase()) }), true),
    toggleSelected: (family, exclusive = false) =>
      update((s) => {
        const f = family.toLowerCase();
        if (exclusive)
          return { ...s, selection: s.selection.length === 1 && s.selection[0] === f ? [] : [f] };
        return {
          ...s,
          selection: s.selection.includes(f) ? s.selection.filter((x) => x !== f) : [...s.selection, f],
        };
      }, true),
    clearSelection: () => update((s) => ({ ...s, selection: [] }), true),
    setLive: (live) => update((s) => ({ ...s, live })),
    setCurrency: (currency) => update((s) => ({ ...s, currency })),
  };
}
